import { useRef, useState, type ReactNode } from 'react'
import {
  motion,
  useInView,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  type Variants,
} from 'motion/react'
import { cn } from '@/lib/utils'
import { useMediaQuery } from '@/components/motion'
import { BRAND, OUT_EASE, WIPE_EASE, type BrandColor } from './halftone'

export interface StickyStep {
  title: string
  description: string
  /** Optional block under the copy (tags, outcome list). */
  extra?: ReactNode
  /** Square visual. `active` is true while the step is on stage (or in view in flow mode). */
  visual: (active: boolean) => ReactNode
}

interface StickyFeaturesProps {
  steps: StickyStep[]
  /** Progress-bar colour. */
  accent?: BrandColor
  className?: string
}

/** Scroll distance each step holds the stage, in viewport heights. */
const STEP_SCROLL = 150

const textVariants: Variants = {
  before: { opacity: 0, y: -30, transition: { duration: 0.35, ease: OUT_EASE } },
  after: { opacity: 0, y: 30, transition: { duration: 0.35, ease: OUT_EASE } },
  active: (k: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: OUT_EASE, delay: 0.18 + k * 0.07 },
  }),
}

const pad = (n: number) => String(n + 1).padStart(2, '0')

function StepNumber({ index }: { index: number }) {
  return (
    <span
      aria-hidden
      className="block font-[family-name:var(--font-landing-subdisplay)] text-[clamp(4rem,7vw,7.5rem)] font-light leading-none tracking-[-0.04em] text-black/10 dark:text-white/15"
    >
      {pad(index)}
    </span>
  )
}

function StepCopy({ step }: { step: StickyStep }) {
  return (
    <>
      <h3 className="landing-text-display-lg text-[var(--color-landing-gray-900)] dark:text-white">
        {step.title}
      </h3>
      <p className="landing-text-body-lg mt-5 max-w-[46ch] text-pretty text-[var(--color-landing-gray-500)] dark:text-[var(--color-landing-gray-400)]">
        {step.description}
      </p>
    </>
  )
}

/**
 * Pinned feature stage (reference "sticky features"): a 100vh stage held for
 * count × 150% of scroll. Left, the step texts share one spot and crossfade
 * (out moving up, in rising from below, staggered). Right, the square
 * visuals share one spot and open from clip-path inset(50%) to inset(0);
 * a bar under them tracks scroll progress.
 *
 * CSS sticky + motion useScroll, no pinning library. Below lg and under
 * reduced motion the steps render in normal flow, complete and static.
 */
export function StickyFeatures({ steps, accent = 'blue', className }: StickyFeaturesProps) {
  const wide = useMediaQuery('(min-width: 1024px)')
  const reduced = useReducedMotion()
  if (!wide || reduced) return <FlowSteps steps={steps} className={className} />
  return <PinnedSteps steps={steps} accent={accent} className={className} />
}

function PinnedSteps({ steps, accent = 'blue', className }: StickyFeaturesProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const count = steps.length
  const [active, setActive] = useState(0)
  const entered = useInView(stageRef, { once: true, amount: 0.5 })
  const { scrollYProgress } = useScroll({ target: trackRef, offset: ['start start', 'end end'] })

  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    const next = Math.min(count - 1, Math.max(0, Math.floor(v * count)))
    setActive((prev) => (prev === next ? prev : next))
  })

  return (
    <div
      ref={trackRef}
      className={cn('relative', className)}
      style={{ height: `calc(100svh + ${count * STEP_SCROLL}svh)` }}
    >
      <div ref={stageRef} className="sticky top-0 flex h-[100svh] items-center overflow-hidden">
        <div className="landing-container grid grid-cols-2 items-stretch gap-16 xl:gap-24">
          {/* Text: every step in one grid cell, crossfading */}
          <div className="grid">
            {steps.map((step, i) => {
              const state = i === active ? 'active' : i < active ? 'before' : 'after'
              return (
                <motion.div
                  key={step.title}
                  initial={false}
                  animate={state}
                  className={cn(
                    'col-start-1 row-start-1 flex flex-col justify-between',
                    i !== active && 'pointer-events-none'
                  )}
                  aria-current={i === active ? 'step' : undefined}
                >
                  <motion.div variants={textVariants} custom={0}>
                    <StepNumber index={i} />
                  </motion.div>
                  <div className="pb-6">
                    <motion.div variants={textVariants} custom={1}>
                      <h3 className="landing-text-display-lg text-[var(--color-landing-gray-900)] dark:text-white">
                        {step.title}
                      </h3>
                    </motion.div>
                    <motion.p
                      variants={textVariants}
                      custom={2}
                      className="landing-text-body-lg mt-5 max-w-[46ch] text-pretty text-[var(--color-landing-gray-500)] dark:text-[var(--color-landing-gray-400)]"
                    >
                      {step.description}
                    </motion.p>
                    {step.extra && (
                      <motion.div variants={textVariants} custom={3} className="mt-6">
                        {step.extra}
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              )
            })}
          </div>

          {/* Visuals: stacked squares opening from the centre, then the progress bar */}
          <div className="ml-auto w-full max-w-[calc(100svh-11rem)]">
            <div className="relative aspect-square w-full">
              {steps.map((step, i) => {
                const open = i === 0 ? entered : i <= active
                return (
                  <motion.div
                    key={step.title}
                    className="absolute inset-0"
                    initial={false}
                    animate={{ clipPath: open ? 'inset(0% 0% 0% 0%)' : 'inset(50% 50% 50% 50%)' }}
                    transition={{ duration: 0.75, ease: WIPE_EASE }}
                  >
                    {step.visual(entered && i === active)}
                  </motion.div>
                )
              })}
            </div>
            <div className="h-1 w-full" aria-hidden>
              <motion.div
                className="h-full w-full origin-left"
                style={{ scaleX: scrollYProgress, backgroundColor: BRAND[accent] }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function FlowVisual({ render }: { render: StickyStep['visual'] }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, amount: 0.4 })
  return (
    <div ref={ref} className="relative aspect-square w-full max-w-xl">
      {render(inView)}
    </div>
  )
}

function FlowSteps({ steps, className }: { steps: StickyStep[]; className?: string }) {
  return (
    <ol className={cn('landing-container space-y-20 md:space-y-28', className)}>
      {steps.map((step, i) => (
        <li key={step.title} className="grid items-center gap-8 md:grid-cols-2 md:gap-12">
          <div>
            <StepNumber index={i} />
            <div className="mt-6">
              <StepCopy step={step} />
              {step.extra && <div className="mt-6">{step.extra}</div>}
            </div>
          </div>
          <FlowVisual render={step.visual} />
        </li>
      ))}
    </ol>
  )
}

export default StickyFeatures
